import {
  adjustGroupId,
  FilterGroup,
  FilterGroupItem,
  POSGroupFilter,
  POSGroupFilterType,
} from '@publicisgroupe/p-os-components';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FieldsType } from '../../constants/FieldsType';
import useGetEntityFieldValuesQuery from '../../hooks/queries/useGetEntityFieldValuesQuery';
import { EntityMetadata } from '../../interfaces/EntityMetadata';
import { Field, XdmContextInfo } from '../../interfaces/Field';
import { getField } from '../../utils/getField';
import { getEntityPropertyName } from '../../utils/xdmUtils';
import { Entity } from '../../interfaces/Entity';
import { xdmContextDelimiter } from '@publicisgroupe/p-os-components/build/components/POSGroupFilter/POSGroupFilterConstants';

interface FiltersConfigurationProps {
  metadata: EntityMetadata;
  entityName: string;
  selectedFilterItems: FilterGroupItem[];
  onSelectedFilterItemsChanged: (filterItems: FilterGroupItem[]) => void;
}

export const FiltersConfiguration: React.FC<FiltersConfigurationProps> = ({
  metadata,
  entityName,
  selectedFilterItems,
  onSelectedFilterItemsChanged,
}) => {
  const { register, control } = useForm({
    mode: 'onSubmit',
  });

  const [selectedFilterGroup, setSelectedFilterGroup] = useState<FilterGroup>();
  const [filterTabActiveIndex, setFilterTabActiveIndex] = useState(0);
  const [allFilterItems, setAllFilterItems] = useState<FilterGroupItem[]>([]);
  const [currentFilterItems, setCurrentFilterItems] = useState<
    FilterGroupItem[]
  >(selectedFilterItems || []);

  const filterGroups = useMemo(() => {
    if (!metadata) return [];

    const filterableFields: Field[] =
      metadata.fields.filter((field) => !field.ignore) ?? [];
    return (
      filterableFields.map((field) => {
        return {
          groupId: field.name,
          groupName: field.name
            .toLowerCase()
            .replace(/(?:^|\s)\S/g, (char) => char.toUpperCase()),
          groupType:
            field.type == FieldsType.xdm || field.type === FieldsType.xdmContext
              ? POSGroupFilterType.Custom
              : undefined,
        };
      }) ?? []
    );
  }, [metadata]);

  const {
    data: fieldValuesData,
    isLoading: isLoadingFieldValues,
    isFetchedAfterMount: isFetchedFieldValues,
  } = useGetEntityFieldValuesQuery(
    entityName,
    selectedFilterGroup?.groupType != POSGroupFilterType.Custom
      ? selectedFilterGroup?.groupId ?? ''
      : ''
  );

  useEffect(() => {
    if (fieldValuesData && isFetchedFieldValues && selectedFilterGroup) {
      const filterItems = fieldValuesData.map((fieldValue) => {
        return {
          itemId: `${selectedFilterGroup?.groupId}-${fieldValue}`,
          itemName: fieldValue,
          groupId: selectedFilterGroup?.groupId ?? '',
        };
      });

      setAllFilterItems((currentFilterItems) => {
        const uniqueItems = filterItems.filter(
          (uniqueItem) =>
            !currentFilterItems.some(
              (currentItem) => uniqueItem.itemId === currentItem.itemId
            )
        );
        return [...currentFilterItems, ...uniqueItems];
      });
    }
  }, [fieldValuesData, isFetchedFieldValues, selectedFilterGroup]);

  const getFilterGroup = useCallback(
    (groupId: string) => {
      return filterGroups.find(
        (filterGroup) => filterGroup?.groupId === groupId
      );
    },
    [filterGroups]
  );

  const getFilterGroupFieldInfo = useCallback(
    (filterGroupId: string) => {
      return metadata?.fields.find((field) => field.name === filterGroupId);
    },
    [metadata]
  );

  const getXdmContextFieldInfo = useCallback(
    (fieldName: string, xdmContextField?: XdmContextInfo | null) => {
      if (!xdmContextField) return null;
      return xdmContextField.fields.find((field) => field.name === fieldName);
    },
    []
  );

  const getXdmFieldIdPropertyName = useCallback((xdmFieldInfo: Field) => {
    const defaultIdPropertyName = 'id';
    if (!xdmFieldInfo.xdm) return defaultIdPropertyName;

    return (
      getEntityPropertyName(xdmFieldInfo.xdm.mappings, defaultIdPropertyName) ??
      defaultIdPropertyName
    );
  }, []);

  const getXdmContextChildField = useCallback(
    (xdmContextFieldInfo: Field, fieldName: string) => {
      if (!xdmContextFieldInfo.xdmContext) return null;
      return xdmContextFieldInfo.xdmContext.fields.find(
        (x) => x.name == fieldName
      );
    },
    []
  );

  const getXdmFieldItems = useCallback(
    (xdmFieldInfo: Field, xdmValues: any[]) => {
      if (!xdmFieldInfo.xdm) return [];

      const idProperty = getXdmFieldIdPropertyName(xdmFieldInfo);
      const groupName = xdmFieldInfo.name;
      return xdmValues.map((value) => ({
        itemId: `${groupName}-${value[idProperty]}`,
        itemName: value[idProperty],
        groupId: groupName ?? '',
      }));
    },
    [getXdmFieldIdPropertyName]
  );

  const getXdmContextFieldItems = useCallback(
    (xdmContextFieldInfo: Field, xdmContextValues: any) => {
      const combinedXdmContextItems: any[] = [];
      if (!xdmContextFieldInfo.xdmContext) return combinedXdmContextItems;

      Object.keys(xdmContextValues).forEach((fieldName) => {
        const fieldInfo = getXdmContextFieldInfo(
          fieldName,
          xdmContextFieldInfo.xdmContext
        );
        if (!fieldInfo) return;

        const idProperty = getXdmFieldIdPropertyName(fieldInfo);
        const groupName = `${xdmContextFieldInfo?.name}${xdmContextDelimiter}${fieldName}`;
        const currentFieldItems: any[] = [];
        if (Array.isArray(xdmContextValues[fieldName])) {
          currentFieldItems.push(...xdmContextValues[fieldName]);
        } else {
          currentFieldItems.push(xdmContextValues[fieldName]);
        }
        const mappedItems = currentFieldItems
          .filter((value) => !!value)
          .map((value) => ({
            itemId: `${groupName}-${value[idProperty]}`,
            itemName: value[idProperty],
            groupId: groupName ?? '',
          }));
        combinedXdmContextItems.push(...mappedItems);
      });
      return combinedXdmContextItems;
    },
    [getXdmContextFieldInfo, getXdmFieldIdPropertyName]
  );

  const onCustomFieldChange = useCallback(
    (newValues: any[], selectedGroupId: string) => {
      const fieldInfo = getFilterGroupFieldInfo(selectedGroupId);
      if (!fieldInfo) return;

      let itemsInSelectedGroup: any[] = [];
      if (fieldInfo.xdm) {
        itemsInSelectedGroup = getXdmFieldItems(fieldInfo, newValues);
        setCurrentFilterItems((currItems) => {
          const itemsNotInSelectedGroup = currItems.filter(
            (item) => item.groupId != selectedGroupId
          );
          return [...itemsNotInSelectedGroup, ...itemsInSelectedGroup];
        });
      }

      if (fieldInfo.xdmContext) {
        itemsInSelectedGroup = getXdmContextFieldItems(fieldInfo, newValues);
        setCurrentFilterItems((currItems) => {
          const itemsNotInSelectedGroup = currItems.filter(
            // For xdmContext groupId would look like xdmContextFieldName::SubfieldName
            // So we need to compare only the part before '::'
            (item) => adjustGroupId(item.groupId) != selectedGroupId
          );
          return [...itemsNotInSelectedGroup, ...itemsInSelectedGroup];
        });
      }
    },
    [getFilterGroupFieldInfo, getXdmContextFieldItems, getXdmFieldItems]
  );

  const getFilterItemCustomContent = useCallback(
    (filterGroup: FilterGroup) => {
      const fieldInfo = getFilterGroupFieldInfo(filterGroup.groupId);
      if (!fieldInfo) return <></>;

      let defaultValues: any;
      if (fieldInfo.xdm) {
        const idProperty = getXdmFieldIdPropertyName(fieldInfo);
        defaultValues = currentFilterItems
          .filter((item) => item.groupId == filterGroup.groupId)
          .map(
            (item) =>
              ({
                [idProperty]: item.itemName,
              } as Entity)
          );
      }
      if (fieldInfo.xdmContext) {
        defaultValues = currentFilterItems
          .filter((item) => adjustGroupId(item.groupId) == filterGroup.groupId)
          .reduce((acc, item) => {
            const childFieldName = item.groupId.split(xdmContextDelimiter)[1];
            const childField = getXdmContextChildField(
              fieldInfo,
              childFieldName
            );
            if (!childField) return;

            const idProperty = getXdmFieldIdPropertyName(childField);
            const isArray = childField.isArray ?? false;
            const newItem = { [idProperty]: item.itemName };
            if (!acc[childFieldName]) {
              acc[childFieldName] = isArray ? [newItem] : newItem;
            } else {
              acc[childFieldName] = [...acc[childFieldName], newItem];
            }

            return acc;
          }, {} as any);
      }
      return (
        <Controller
          {...register(fieldInfo.name as never)}
          control={control}
          render={({ field: formField, formState }) => {
            return getField(
              fieldInfo,
              formField,
              formState,
              true,
              onCustomFieldChange,
              defaultValues
            );
          }}
        />
      );
    },
    [
      getFilterGroupFieldInfo,
      register,
      control,
      getXdmFieldIdPropertyName,
      currentFilterItems,
      getXdmContextChildField,
      onCustomFieldChange,
    ]
  );

  const buildCustomFilterItem = useCallback(
    (filterGroup?: FilterGroup) => {
      if (filterGroup?.groupType == POSGroupFilterType.Custom) {
        const customItemId = `${filterGroup.groupId}-${POSGroupFilterType.Custom}`;
        const currentFilterItems = [...allFilterItems];
        const itemIndex = currentFilterItems.findIndex(
          (item) => item.itemId === customItemId
        );

        if (itemIndex == -1) {
          const customFilterItem: FilterGroupItem = {
            itemName: customItemId,
            itemId: customItemId,
            groupId: filterGroup.groupId,
            content: getFilterItemCustomContent(filterGroup),
          };

          setAllFilterItems((currentItems) => [
            ...currentItems,
            customFilterItem,
          ]);
        }
      }
    },
    [allFilterItems, getFilterItemCustomContent]
  );

  const handleFilterOnTabChange = useCallback(
    (groupId: string, activeIndex: number) => {
      const filterGroup = getFilterGroup(groupId);
      setSelectedFilterGroup(filterGroup);
      setFilterTabActiveIndex(activeIndex);
      buildCustomFilterItem(filterGroup);
    },
    [buildCustomFilterItem, getFilterGroup]
  );

  const handleFilterReset = useCallback(() => {
    setFilterTabActiveIndex(0);
    onSelectedFilterItemsChanged([]);
  }, [onSelectedFilterItemsChanged]);

  const handleApplyFilters = useCallback(
    (filterItems: FilterGroupItem[]) => {
      onSelectedFilterItemsChanged(filterItems ?? []);
    },
    [onSelectedFilterItemsChanged]
  );

  useEffect(() => {
    if (!selectedFilterGroup && filterGroups.length > 0)
      setSelectedFilterGroup(filterGroups[0]);
  }, [filterGroups, selectedFilterGroup]);

  return (
    <POSGroupFilter
      filterGroups={filterGroups}
      filterItems={allFilterItems ?? []}
      defaultSelectedItems={currentFilterItems}
      selectedTabIndex={filterTabActiveIndex}
      onTabChange={handleFilterOnTabChange}
      onApplyFilters={handleApplyFilters}
      onFilterChange={setCurrentFilterItems}
      onReset={handleFilterReset}
      tabPaneLoadingText={
        isLoadingFieldValues || !allFilterItems ? 'Loading...' : ''
      }
    />
  );
};
